import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { playlistTagService } from "../api/services/playlistTagService";
import {
  PlaylistTagFilters,
  PlaylistTagsResponse,
  UpdatePlaylistTagPayload,
} from "../schemas/playlistTagSchema";

export const usePlaylistTags = (
  filters: PlaylistTagFilters = {},
  options?: { enabled?: boolean },
) => {
  const queryClient = useQueryClient();

  const {
    data: playlistTags,
    isLoading: isPlaylistTagsLoading,
    refetch,
  } = useQuery<PlaylistTagsResponse, Error>({
    queryKey: ["playlistTags", filters],
    queryFn: () => playlistTagService.getAll(filters),
    enabled: options?.enabled !== false,
  });

  const createPlaylistTagMutation = useMutation({
    mutationFn: playlistTagService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["playlistTags"] });
      queryClient.invalidateQueries({ queryKey: ["playlistTagOptions"] });
      toast.success("Playlist Tag created successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create Playlist Tag");
    },
  });

  const updatePlaylistTagMutation = useMutation({
    mutationFn: playlistTagService.update,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["playlistTags"] });
      queryClient.invalidateQueries({ queryKey: ["playlistTagOptions"] });
      toast.success("Playlist Tag updated successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update Playlist Tag");
    },
  });

  const deletePlaylistTagMutation = useMutation({
    mutationFn: playlistTagService.remove,
    onSuccess: (_data, ids) => {
      queryClient.invalidateQueries({ queryKey: ["playlistTags"] });
      queryClient.invalidateQueries({ queryKey: ["playlistTagOptions"] });
      if (Array.isArray(ids) && ids.length > 1) {
        toast.success(`${ids.length} items deleted successfully`);
        return;
      }
      toast.success("Playlist Tag deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete Playlist Tag");
    },
  });

  const deletePlaylistTags = (ids: string[]) => {
    deletePlaylistTagMutation.mutate(ids);
  };

  return {
    playlistTags,
    refetchPlaylistTags: refetch,
    createPlaylistTag: createPlaylistTagMutation.mutate,
    updatePlaylistTag: updatePlaylistTagMutation.mutate,
    deletePlaylistTags,
    isLoading:
      isPlaylistTagsLoading ||
      createPlaylistTagMutation.isPending ||
      updatePlaylistTagMutation.isPending ||
      deletePlaylistTagMutation.isPending,
  };
};
